import { useState, useRef, useEffect } from 'react';

function CustomDropdown({
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  style = {},
  required = false,
  disabled = false,
  creatable = false,
  onCreate = null,
  createLabel = 'Create'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option && option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if we should show the "Create new" option
  const showCreateOption = creatable &&
    searchTerm.trim() !== '' &&
    !filteredOptions.some(opt => opt && opt.label && opt.label.toLowerCase() === searchTerm.toLowerCase()) &&
    onCreate;

  // Get display label for selected value
  const selectedOption = options.find(opt => opt && opt.value === value);
  const displayLabel = selectedOption && selectedOption.label ? selectedOption.label : placeholder;

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCreate = async () => {
    if (!onCreate || !searchTerm.trim()) return;

    setIsCreating(true);
    try {
      await onCreate(searchTerm.trim());
      setIsOpen(false);
      setSearchTerm('');
    } catch (error) {
      console.error('Error creating new option:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const containerStyle = {
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box',
    ...style
  };

  const dropdownButtonStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    borderColor: (isOpen || isHovered) && !disabled ? '#C8342B' : '#ddd',
    borderRadius: '6px',
    background: disabled ? '#f5f5f5' : 'white',
    color: value ? '#3D1F1F' : '#7f8c8d',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    fontWeight: value ? '500' : '400',
    outline: 'none',
    boxSizing: 'border-box',
    boxShadow: isOpen && !disabled ? '0 0 0 3px rgba(200, 51, 43, 0.1)' : 'none'
  };

  const arrowStyle = {
    width: 0,
    height: 0,
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
    borderTop: `5px solid ${isOpen ? '#C8342B' : '#7f8c8d'}`,
    transition: 'all 0.2s ease',
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
  };

  const dropdownMenuStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    background: 'white',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    borderColor: '#C8342B',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    maxHeight: '250px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'slideDown 0.2s ease'
  };

  const searchInputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderWidth: '0 0 1px 0',
    borderStyle: 'solid',
    borderColor: '#eee',
    fontSize: '14px',
    outline: 'none',
    color: '#3D1F1F',
    background: '#f9f9f9',
    boxSizing: 'border-box'
  };

  const optionStyle = (isSelected) => ({
    padding: '10px 12px',
    cursor: 'pointer',
    fontSize: '14px',
    color: isSelected ? 'white' : '#3D1F1F',
    background: isSelected ? '#C8342B' : 'white',
    fontWeight: isSelected ? '600' : '400',
    transition: 'all 0.15s ease',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: isSelected ? '#8B2419' : 'transparent'
  });

  const emptyStateStyle = {
    padding: '20px 12px',
    textAlign: 'center',
    color: '#7f8c8d',
    fontSize: '13px'
  };

  const createOptionStyle = {
    padding: '10px 12px',
    cursor: isCreating ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    color: 'white',
    background: isCreating ? '#95a5a6' : '#27ae60',
    fontWeight: '600',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#1e8449',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.15s ease'
  };

  return (
    <>
      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div ref={dropdownRef} style={containerStyle}>
        <div
          style={dropdownButtonStyle}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onMouseEnter={() => !disabled && setIsHovered(true)}
          onMouseLeave={() => !disabled && setIsHovered(false)}
        >
          <span>{displayLabel}</span>
          <div style={arrowStyle} />
        </div>

        {isOpen && !disabled && (
          <div style={dropdownMenuStyle}>
            {(options.length > 5 || creatable) && (
              <input
                type="text"
                placeholder={creatable ? "Search or type to create..." : "Search..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={searchInputStyle}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            )}

            {showCreateOption && (
              <div
                style={createOptionStyle}
                onClick={!isCreating ? handleCreate : undefined}
                onMouseEnter={(e) => {
                  if (!isCreating) {
                    e.currentTarget.style.background = '#229954';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCreating) {
                    e.currentTarget.style.background = '#27ae60';
                  }
                }}
              >
                <span style={{ fontSize: '16px' }}>+</span>
                <span>
                  {isCreating ? 'Creating...' : `${createLabel} "${searchTerm}"`}
                </span>
              </div>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  style={optionStyle(option.value === value)}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={(e) => {
                    if (option.value !== value) {
                      e.currentTarget.style.background = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (option.value !== value) {
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  {option.label}
                </div>
              ))
            ) : !showCreateOption && (
              <div style={emptyStateStyle}>
                No options found
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default CustomDropdown;
